import type { ChatReply, ChatRequest } from '@sophia-ai/shared-types';
import { readSseEvents } from './sse';

/** Callbacks driven by the streamed reply, in arrival order. */
export interface ChatHandlers {
  onDelta: (text: string) => void;
  onDone: (reply: ChatReply) => void;
  onError: (message: string) => void;
}

/** Customer-facing fallback copy (Arabic-first), kept out of the components. */
const MESSAGES = {
  network: 'تعذّر الاتصال بالخدمة. تحقّق من اتصالك وحاول مجدداً.',
  server: 'حدث خطأ غير متوقع. حاول مرة أخرى بعد قليل.',
  interrupted: 'انقطع البث قبل اكتمال الرد. حاول مرة أخرى.',
  assistant: 'واجه المساعد مشكلة. حاول مرة أخرى.',
  incomplete: 'انقطع الاتصال قبل اكتمال الرد.',
} as const;

/**
 * Sends one message to our chat API and drives the handlers as the reply
 * streams back. Resolves when the stream ends (success or handled error); never
 * throws — transport failures surface through `onError`.
 */
export async function sendMessage(
  apiBaseUrl: string,
  request: ChatRequest,
  handlers: ChatHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${apiBaseUrl.replace(/\/+$/, '')}/api/chat`;
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  };

  if (signal !== undefined) {
    init.signal = signal;
  }

  let response: Response;

  try {
    response = await fetch(url, init);
  } catch {
    handlers.onError(MESSAGES.network);
    return;
  }

  if (!response.ok || response.body === null) {
    handlers.onError(MESSAGES.server);
    return;
  }

  // Track whether the server sent a terminal frame (done/error). If the stream
  // closes cleanly without one — e.g. a proxy idle-timeout or a worker exiting
  // after flushing deltas — we must still end the turn, or the bubble stays
  // stuck "streaming" forever.
  let terminalReceived = false;

  try {
    for await (const event of readSseEvents(response.body)) {
      if (event.type === 'delta') {
        handlers.onDelta(event.text);
      } else if (event.type === 'done') {
        terminalReceived = true;
        handlers.onDone(event.reply);
      } else {
        // The widget owns all customer-facing copy; the server's English error
        // string is for logs only.
        terminalReceived = true;
        handlers.onError(MESSAGES.assistant);
      }
    }
  } catch {
    // Aborts are intentional (panel closed / new message); stay silent.
    if (signal?.aborted !== true) {
      handlers.onError(MESSAGES.interrupted);
    }
    return;
  }

  if (!terminalReceived) {
    handlers.onError(MESSAGES.incomplete);
  }
}
